import rp, { SqlCompletionOptions, SqlEditorAdapter, SqlEditorOptions, SqlExecOptions, SqlExecCallbackArgs, SqlParseResult } from "@reporting/rp-app-host";
import { AccountingRights } from "@reporting/rp-types/core";
import { JobStepSubsystem } from "../types";

/**
 * Editor of an Agent job step's command. The step runs as a raw batch under SQL Server Agent - no
 * p_ETL_EXEC_THREAD header, no {placeholder} or parameter substitution - so parse and exec go through
 * rp-ms-server, which runs the text exactly as written. Exec runs under the accounting's ETL login,
 * not the Agent service account, so object permissions may still differ from a scheduled run.
 */
export class JobStepSqlEditorAdapter extends SqlEditorAdapter {

	constructor(
		private jobName: string,
		private stepId: number,
		private subsystem: string = JobStepSubsystem.TSQL,
	) { super() }

	/** only a T-SQL step can be parsed or executed as SQL; a PowerShell step is just text here */
	private get isTsql(): boolean {
		return (this.subsystem || JobStepSubsystem.TSQL) === JobStepSubsystem.TSQL;
	}

	async getContent() : Promise<string> {
		return await rp.ms('jobs').get<string>(`steps/${this.stepId}/content`, { job_name: this.jobName }) || '';
	}

	async save(sql: string) {
		await rp.ms('jobs').patch(`steps/${this.stepId}/content`, sql, { job_name: this.jobName });
	}

	async parse(sql: string) : Promise<SqlParseResult> {
		// a PowerShell step is not SQL at all - null switches the editor's parsing off
		if (!this.isTsql) return null;
		return rp.ms('server').post<SqlParseResult>('parse', sql);
	}

	async exec(sql: string, options?: SqlExecOptions, cb?: (args: SqlExecCallbackArgs) => void) : Promise<void> {
		// no query plan for job steps - only the row limit is passed on
		await this.execStream(rp.ms('server'), 'exec', sql, { top: options?.top }, cb);
	}

	async abort() : Promise<void> {
		await this.abortSpid(rp.ms('server'));
	}

	/** a raw Agent batch has neither the engine's @variables nor {placeholders} or {{macros}}, so they are not offered */
	getCompletionOptions(): SqlCompletionOptions {
		return { internalVariables: false, pseudoVariables: false, macros: false };
	}

	getOptions(): SqlEditorOptions {
		const canDefine = rp.userProvider.hasRight(AccountingRights.JobsDefine);
		const canExec = canDefine && this.isTsql;
		return this.createOptions({
			history: false,
			readOnly: !canDefine,
			toolbar: {
				// plain Execute button: no Compile (raw batch) and no Explain (no query plan for job steps)
				exec: { enabled: canExec, items: null },
				top: { enabled: canExec },
			},
		});
	}

}
